import { useEffect, useRef, useState } from 'react';
import { Preloader } from 'konsta/react';
import { CheckCircle2, Clock, Flag, MoreHorizontal, ShieldBan, Wallet } from 'lucide-react';
import { REPORT_CATEGORIES } from '@harmony/shared';
import { api, ApiError, actionId, type ProviderCard, type Quota } from '../api';
import { BottomSheet, EmptyState, PrimaryButton, Rating, TopBar, Where, etb, useToast } from '../components/ui';
import { FavoriteHeart, PhotoCarousel } from '../components/ProviderPost';
import { inputClass } from '../components/form';
import { confirmDialog, haptic } from '../telegram';
import { useNav } from '../nav';
import { useErrorText, useLang, useT } from '../i18n';
import { isOn, useSettings } from '../settings';
import { WalletPage } from './Wallet';

/**
 * Full provider profile. Clients can book, save, report or block.
 * Providers and agents see the same page without those actions.
 */
export function ProviderProfile({
  id,
  initial,
  canBook,
  onChanged,
}: {
  id: string;
  initial?: ProviderCard;
  canBook: boolean;
  onChanged?: () => void;
}) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const errText = useErrorText();
  const [p, setP] = useState<ProviderCard | null>(initial ?? null);
  const [missing, setMissing] = useState(false);
  const [sheet, setSheet] = useState<null | 'book' | 'more' | 'report'>(null);

  useEffect(() => {
    api.provider(id).then(setP).catch(() => setMissing(true));
  }, [id]);

  if (missing)
    return (
      <div className="h-full">
        <TopBar />
        <EmptyState icon="🙈" text={t('err.NOT_FOUND')} />
      </div>
    );
  if (!p)
    return (
      <div className="h-full">
        <TopBar />
        <div className="flex justify-center pt-24">
          <Preloader />
        </div>
      </div>
    );

  const block = async () => {
    if (!(await confirmDialog(t('block.confirm', { name: p.displayName })))) return;
    try {
      await api.block(p.id);
      haptic('success');
      toast(t('block.done'));
      onChanged?.();
      nav.pop();
    } catch (e) {
      toast(errText(e));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <TopBar
        title={p.displayName}
        right={
          canBook ? (
            <button onClick={() => setSheet('more')} className="flex h-10 w-10 items-center justify-center rounded-full active:bg-tg-secondary" aria-label="more">
              <MoreHorizontal size={22} />
            </button>
          ) : undefined
        }
      />
      {canBook ? (
        // Clients see the whole profile and the Book button on one screen: the photo takes whatever height is left.
        <FitProfile p={p} onChanged={onChanged} onBook={() => setSheet('book')} />
      ) : (
        <div className="flex-1 overflow-y-auto pb-10">
          <ProfileBody p={p} canBook={canBook} onChanged={onChanged} />
          <div className="px-4">
            <p className="mt-5 rounded-2xl bg-tg-secondary p-3 text-sm text-tg-hint">{t('provider.view_only')}</p>
          </div>
        </div>
      )}

      <BookSheet open={sheet === 'book'} p={p} onClose={() => setSheet(null)} />
      <BottomSheet open={sheet === 'more'} onClose={() => setSheet(null)}>
        <button className="flex w-full items-center gap-3 rounded-xl px-2 py-3.5 text-left text-[16px] active:bg-tg-secondary" onClick={() => setSheet('report')}>
          <Flag size={20} /> {t('provider.report')}
        </button>
        <button className="flex w-full items-center gap-3 rounded-xl px-2 py-3.5 text-left text-[16px] text-red-600 active:bg-tg-secondary" onClick={block}>
          <ShieldBan size={20} /> {t('provider.block')}
        </button>
      </BottomSheet>
      <ReportSheet open={sheet === 'report'} p={p} onClose={() => setSheet(null)} />
    </div>
  );
}

/**
 * One-screen profile for clients: photos shrink to fit, then name, rating, distance,
 * a two-line bio, prices and the Book button, all visible without scrolling.
 */
function FitProfile({ p, onChanged, onBook }: { p: ProviderCard; onChanged?: () => void; onBook: () => void }) {
  const t = useT();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative min-h-[160px] flex-1 overflow-hidden bg-tg-secondary">
        <PhotoCarousel photos={p.photos} ratio="h-full" />
        <span
          className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow ${
            p.available ? 'bg-green-600 text-white' : 'bg-black/60 text-white'
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${p.available ? 'bg-white' : 'bg-gray-300'}`} />
          {p.available ? t('provider.available') : t('provider.unavailable')}
        </span>
      </div>
      <div className="shrink-0 px-4 pb-3 pt-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-bold leading-tight">
              {p.displayName}, <span className="font-normal">{p.age}</span>
            </h1>
            <div className="mt-0.5 flex min-w-0 items-center gap-x-3">
              <Rating avg={p.ratingAvg} count={p.ratingCount} />
              <Where p={p} />
            </div>
          </div>
          <FavoriteHeart p={p} size={26} onChange={() => onChanged?.()} />
        </div>
        {p.bio && <p className="mt-2 line-clamp-2 text-[15px] leading-snug">{p.bio}</p>}
        <div className="mt-2.5 flex gap-2">
          {p.prices.map((x) => (
            <div key={x.minutes} className="flex-1 rounded-xl bg-tg-secondary px-3 py-2">
              <div className="flex items-center gap-1 text-xs text-tg-hint">
                <Clock size={12} /> {t('provider.min', { minutes: x.minutes })}
              </div>
              <div className="text-[17px] font-bold">{etb(x.price)} ETB</div>
            </div>
          ))}
        </div>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-tg-hint">
          <Wallet size={12} /> {t('book.pay_in_person')}
        </p>
      </div>
      <div className="shrink-0 border-t border-black/5 bg-tg-bg px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
        <PrimaryButton disabled={!p.available} onClick={onBook}>
          {p.available ? t('provider.book') : t('provider.unavailable')}
        </PrimaryButton>
      </div>
    </div>
  );
}

/** Photos, name, rating, availability, bio and prices — shared by the client view and the provider's own Profile tab. */
export function ProfileBody({ p, canBook, onChanged }: { p: ProviderCard; canBook: boolean; onChanged?: () => void }) {
  const t = useT();
  return (
    <>
      <PhotoCarousel photos={p.photos} />
      <div className="px-4 pt-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold">
              {p.displayName}, <span className="font-normal">{p.age}</span>
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <Rating avg={p.ratingAvg} count={p.ratingCount} />
              <Where p={p} />
            </div>
          </div>
          {canBook && <FavoriteHeart p={p} size={28} onChange={() => onChanged?.()} />}
        </div>
        <div
          className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
            p.available ? 'bg-green-500/15 text-green-700' : 'bg-tg-secondary text-tg-hint'
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${p.available ? 'bg-green-500' : 'bg-gray-400'}`} />
          {p.available ? t('provider.available') : t('provider.unavailable')}
        </div>

        {p.bio && (
          <section className="mt-5">
            <h2 className="mb-1 text-sm font-semibold text-tg-hint">{t('provider.about')}</h2>
            <p className="text-[16px] leading-relaxed">{p.bio}</p>
          </section>
        )}

        <section className="mt-5">
          <h2 className="mb-2 text-sm font-semibold text-tg-hint">{t('provider.prices')}</h2>
          <div className="grid grid-cols-2 gap-2">
            {p.prices.map((x) => (
              <div key={x.minutes} className="rounded-2xl bg-tg-secondary p-3">
                <div className="flex items-center gap-1.5 text-sm text-tg-hint">
                  <Clock size={14} /> {t('provider.min', { minutes: x.minutes })}
                </div>
                <div className="text-xl font-bold">{etb(x.price)} ETB</div>
              </div>
            ))}
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-tg-hint">
            <Wallet size={14} /> {t('book.pay_in_person')}
          </p>
        </section>
      </div>
    </>
  );
}

/** Choose 60 or 120 minutes and send a request. Handles "no requests left". */
function BookSheet({ open, p, onClose }: { open: boolean; p: ProviderCard; onClose: () => void }) {
  const t = useT();
  const lang = useLang();
  const nav = useNav();
  const errText = useErrorText();
  const [minutes, setMinutes] = useState<number | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [state, setState] = useState<'choose' | 'sending' | 'sent' | 'empty'>('choose');
  const [error, setError] = useState<string | null>(null);
  const key = useRef(actionId());
  const settings = useSettings();
  const canBuy = isOn(settings, 'feature.client_wallet_enabled') && isOn(settings, 'feature.client_payment_enabled');

  useEffect(() => {
    if (!open) return;
    setState('choose');
    setError(null);
    setMinutes(p.prices.length === 1 ? p.prices[0].minutes : null);
    key.current = actionId(); // a new tap on Book is a new request
    api.quota().then((q) => {
      setQuota(q);
      if (q.total === 0) setState('empty');
    }).catch(() => undefined);
  }, [open]);

  const send = async () => {
    if (!minutes) return;
    setState('sending');
    setError(null);
    try {
      const r = await api.book(p.id, minutes, key.current);
      setQuota(r.quota);
      haptic('success');
      setState('sent');
    } catch (e) {
      haptic('error');
      if (e instanceof ApiError && e.code === 'NO_CREDITS') {
        setQuota((q) => (q ? { ...q, total: 0, nextRechargeAt: String(e.body.nextRechargeAt ?? q.nextRechargeAt) } : q));
        setState('empty');
      } else {
        setError(errText(e));
        setState('choose');
      }
    }
  };

  const refill = quota ? new Date(quota.nextRechargeAt).toLocaleString(lang === 'am' ? 'am-ET' : 'en-US', { hour: 'numeric', minute: '2-digit', weekday: 'short' }) : '';

  return (
    <BottomSheet open={open} onClose={onClose}>
      {state === 'sent' ? (
        <div className="flex flex-col items-center py-4 text-center">
          <CheckCircle2 size={64} className="mb-3 text-green-500" />
          <h3 className="mb-2 text-xl font-bold">{t('book.sent_title')}</h3>
          <p className="mb-2 text-tg-hint">{t('book.sent_body', { name: p.displayName, expiry: quota?.expiryMinutes ?? 60 })}</p>
          {quota && <p className="mb-5 text-sm font-medium">{t('book.credits_left', { n: quota.total })}</p>}
          <PrimaryButton
            onClick={() => {
              onClose();
              nav.pop();
            }}
          >
            {t('book.keep_looking')}
          </PrimaryButton>
        </div>
      ) : state === 'empty' ? (
        <div className="flex flex-col items-center py-4 text-center">
          <div className="mb-3 text-6xl">⏳</div>
          <h3 className="mb-2 text-xl font-bold">{t('book.no_credits_title')}</h3>
          <p className="mb-5 text-tg-hint">{quota && t('book.no_credits_body', { quota: quota.quota, hours: quota.intervalHours, time: refill })}</p>
          {canBuy && (
            <PrimaryButton
              className="mb-2"
              onClick={() => {
                onClose();
                nav.push('wallet', <WalletPage openBuy />);
              }}
            >
              {t('book.recharge')}
            </PrimaryButton>
          )}
          <PrimaryButton
            onClick={() => {
              onClose();
              nav.pop();
            }}
          >
            {t('book.keep_looking')}
          </PrimaryButton>
        </div>
      ) : (
        <div>
          <h3 className="mb-1 text-xl font-bold">{t('book.title', { name: p.displayName })}</h3>
          <p className="mb-4 text-sm text-tg-hint">{t('book.choose_session')}</p>
          <div className="space-y-2">
            {p.prices.map((x) => (
              <button
                key={x.minutes}
                onClick={() => {
                  haptic('select');
                  setMinutes(x.minutes);
                }}
                className={`flex w-full items-center justify-between rounded-2xl border-2 px-4 py-3.5 text-left ${
                  minutes === x.minutes ? 'border-tg-button bg-tg-secondary' : 'border-transparent bg-tg-secondary'
                }`}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <Clock size={18} /> {t('provider.min', { minutes: x.minutes })}
                </span>
                <span className="text-lg font-bold">{etb(x.price)} ETB</span>
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-tg-hint">{t('book.pay_in_person')}</p>
          {quota && <p className="mt-1 text-sm font-medium">{t('book.credits_left', { n: quota.total })}</p>}
          {error && <p className="mt-3 rounded-xl bg-red-500/10 p-3 text-red-600">{error}</p>}
          <PrimaryButton className="mt-4" disabled={!minutes || state === 'sending'} onClick={send}>
            {state === 'sending' ? <Preloader className="h-5 w-5" /> : t('book.send')}
          </PrimaryButton>
        </div>
      )}
    </BottomSheet>
  );
}

function ReportSheet({ open, p, onClose }: { open: boolean; p: ProviderCard; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const errText = useErrorText();
  const [cat, setCat] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setCat(null);
      setDetails('');
    }
  }, [open]);
  return (
    <BottomSheet open={open} onClose={onClose}>
      <h3 className="mb-1 text-xl font-bold">{t('report.title', { name: p.displayName })}</h3>
      <p className="mb-3 text-sm text-tg-hint">{t('report.reason')}</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {REPORT_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full px-3.5 py-2 text-sm ${cat === c ? 'bg-tg-button text-tg-buttonText' : 'bg-tg-secondary'}`}
          >
            {t(`report.cat.${c}`)}
          </button>
        ))}
      </div>
      <textarea
        className={inputClass + ' min-h-[80px] resize-none'}
        maxLength={500}
        placeholder={t('report.details')}
        value={details}
        onChange={(e) => setDetails(e.target.value)}
      />
      <PrimaryButton
        className="mt-4"
        tone="danger"
        disabled={!cat || busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.report(p.id, cat!, details.trim());
            haptic('success');
            toast(t('report.sent'));
            onClose();
          } catch (e) {
            toast(errText(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('report.send')}
      </PrimaryButton>
    </BottomSheet>
  );
}
